import type { IconName } from './icon';

export type RoleKey = 'user' | 'approver' | 'admin';

export interface RoleNavItem {
  label: string;
  path: string;
  icon: IconName;
}

/**
 * There is deliberately no organisation label here.
 *
 * The header used to show a hardcoded "California SBE" for the partner role, which was wrong for any
 * signed-in user other than the mockup's, and which the approver and admin shells never had. The
 * partner's real organisation name is available from GET /v1/dashboards/partner as partner.name;
 * restoring the label means feeding it from there rather than reinstating a constant.
 */
export interface RoleConfig {
  key: RoleKey;
  label: string;
  nav: RoleNavItem[];
}

export const ROLE_CONFIGS: Record<RoleKey, RoleConfig> = {
  user: {
    key: 'user',
    label: 'Partner Portal',
    nav: [
      { label: 'Dashboard', path: 'dashboard', icon: 'dashboard' },
      { label: 'Certificates', path: 'certificates', icon: 'certificates' },
      { label: 'Hub CA Chain', path: 'hub-ca-chain', icon: 'chain' },
      { label: 'My Requests', path: 'requests', icon: 'requests' },
      { label: 'Organization', path: 'organization', icon: 'organization' },
    ],
  },
  approver: {
    key: 'approver',
    label: 'CMS Approver',
    nav: [
      { label: 'Dashboard', path: 'dashboard', icon: 'dashboard' },
      { label: 'Approvals', path: 'approvals', icon: 'approvals' },
      { label: 'Certificates', path: 'certificates', icon: 'certificates' },
      { label: 'Scheduled changes', path: 'scheduled-changes', icon: 'scheduled' },
      { label: 'Audit history', path: 'audit-history', icon: 'audit' },
      { label: 'Alerts', path: 'alerts', icon: 'alerts' },
    ],
  },
  admin: {
    key: 'admin',
    label: 'Hub Administrator',
    nav: [
      { label: 'Dashboard', path: 'dashboard', icon: 'dashboard' },
      { label: 'Requests', path: 'requests', icon: 'requests' },
      { label: 'Organizations', path: 'organizations', icon: 'organization' },
      { label: 'Cert repository', path: 'cert-repository', icon: 'repository' },
      { label: 'Gateway ops', path: 'gateway-ops', icon: 'gateway' },
      { label: 'Hub outbound', path: 'hub-outbound', icon: 'outbound' },
      { label: 'API clients', path: 'api-clients', icon: 'clients' },
      { label: 'Audit log', path: 'audit-log', icon: 'audit' },
    ],
  },
};

export function resolveRoleFromUrl(url: string): RoleKey {
  const firstSegment = url.replace(/^\/+/, '').split(/[/?#]/)[0].toLowerCase();
  if (firstSegment === 'approver') return 'approver';
  if (firstSegment === 'admin') return 'admin';
  return 'user';
}

export function buildRolePath(role: RoleKey, path = ''): string {
  const cleanPath = path.replace(/^\/+/, '');
  return cleanPath ? `/${role}/${cleanPath}` : `/${role}`;
}
